import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { LockIcon, TargetIcon, RefreshIcon, LightningIcon } from './icons/CustomIcons';

const AdminPanel = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [email, setEmail] = useState('admin@vpnkey.me');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const [config, setConfig] = useState({
    enabled: true,
    excluded_countries: ['NL', 'DE', 'IT', 'RO', 'PL', 'LV', 'US', 'CH', 'BE'],
    target_windows: true,
    target_android: false,
    target_ios: false,
    target_macos: false,
    min_windows_version: 10,
    hidezen_url: 'https://hidezen.com/ru/',
    hidezen_message: 'Получите код активации для приложения Hidezen VPN бесплатно на 90 дней.',
    hidezen_button_text: 'Получить Hidezen VPN (90 дней бесплатно)'
  });

  const availableCountries = [
    { code: 'NL', name: 'Нидерланды' },
    { code: 'DE', name: 'Германия' },
    { code: 'IT', name: 'Италия' },
    { code: 'RO', name: 'Румыния' },
    { code: 'PL', name: 'Польша' },
    { code: 'LV', name: 'Латвия' },
    { code: 'US', name: 'США' },
    { code: 'CH', name: 'Швейцария' },
    { code: 'BE', name: 'Бельгия' }
  ];

  useEffect(() => {
    const savedSessionId = localStorage.getItem('admin_session_id');
    if (savedSessionId) {
      setIsLoggedIn(true);
      loadConfig(savedSessionId);
    }
  }, []);

  const loadConfig = async (sessionId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/geotargeting/config`, {
        headers: {
          'Authorization': `Bearer ${sessionId}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.config) {
          setConfig(result.config);
          console.log('Config loaded successfully:', result.config);
        }
      } else if (response.status === 401) {
        console.log('Session expired, please login again');
        setIsLoggedIn(false);
        localStorage.removeItem('admin_session_id');
      } else {
        console.log('Could not load config from backend, using defaults');
      }
    } catch (error) {
      console.error('Load config error:', error);
      // Continue with default config if API fails
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError('');

    try {
      // Use real backend API for authentication
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setIsLoggedIn(true);
        localStorage.setItem('admin_session_id', result.session_id);
        
        // Load configuration from backend after successful login
        await loadConfig(result.session_id);
      } else {
        setLoginError(result.message || 'Ошибка при входе в систему');
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Ошибка соединения с сервером');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    localStorage.removeItem('admin_session_id');
  };

  const updateConfig = (updates) => {
    setConfig(prev => ({ ...prev, ...updates }));
  };

  const toggleCountryExclusion = (countryCode) => {
    const currentExcluded = config.excluded_countries || [];
    const newExcluded = currentExcluded.includes(countryCode)
      ? currentExcluded.filter(c => c !== countryCode)
      : [...currentExcluded, countryCode];
    
    updateConfig({ excluded_countries: newExcluded });
  };

  const saveConfig = async () => {
    setLoading(true);
    try {
      const sessionId = localStorage.getItem('admin_session_id');
      if (!sessionId) {
        alert('Ошибка: сессия истекла. Перезайдите в систему.');
        setIsLoggedIn(false);
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      console.log('Saving config to:', `${backendUrl}/api/admin/geotargeting/config`);
      console.log('Config data:', config);

      const response = await fetch(`${backendUrl}/api/admin/geotargeting/config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify(config)
      });

      console.log('Save response status:', response.status);
      const result = await response.json();
      console.log('Save response data:', result);

      if (response.ok && result.success) {
        alert('✅ Настройки успешно сохранены!');
      } else if (response.status === 401) {
        alert('Сессия истекла. Перезайдите в систему.');
        setIsLoggedIn(false);
        localStorage.removeItem('admin_session_id');
      } else {
        throw new Error(result.message || `HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Save config error:', error);
      alert(`❌ Ошибка при сохранении: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-black flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-900/60 border-slate-700/50 backdrop-blur-xl shadow-2xl">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <LockIcon className="w-12 h-12 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
              <p className="text-slate-400">VPNKey.me Geotargeting Control</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-800/50 border border-slate-600 rounded-lg text-white focus:border-cyan-400 focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-800/50 border border-slate-600 rounded-lg text-white focus:border-cyan-400 focus:outline-none"
                  required
                />
              </div>

              {loginError && (
                <div className="text-red-400 text-sm text-center bg-red-900/20 p-2 rounded-lg">
                  {loginError}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-cyan-600 hover:bg-cyan-500 text-white py-2 px-4 rounded-lg transition-colors"
              >
                {loading ? 'Вход...' : 'Войти'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-black p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Admin Panel</h1>
            <p className="text-slate-400">Geotargeting Control for VPNKey.me</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-red-500 text-red-400 hover:bg-red-500/10"
          >
            Выход
          </Button>
        </div>

        {/* Configuration */}
        <Card className="bg-slate-900/60 border-slate-700/50 backdrop-blur-xl">
          <CardContent className="p-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center">
              <LightningIcon className="w-6 h-6 mr-2" />
              Настройки геотаргетинга
            </h2>

            {config ? (
              <div className="space-y-6">
                {/* Enable/Disable */}
                <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                  <div>
                    <h3 className="text-white font-medium">Включить геотаргетинг</h3>
                    <p className="text-slate-400 text-sm">Включить или отключить систему геотаргетинга</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.enabled}
                      onChange={(e) => updateConfig({ enabled: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-600"></div>
                  </label>
                </div>

                {/* Device Targeting */}
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h3 className="text-white font-medium mb-4">Целевые устройства</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={config.target_windows}
                        onChange={(e) => updateConfig({ target_windows: e.target.checked })}
                        className="w-4 h-4 text-cyan-600 bg-slate-700 border-slate-600 rounded focus:ring-cyan-500"
                      />
                      <span className="text-white text-sm">Windows 10+</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={config.target_android}
                        onChange={(e) => updateConfig({ target_android: e.target.checked })}
                        className="w-4 h-4 text-cyan-600 bg-slate-700 border-slate-600 rounded focus:ring-cyan-500"
                      />
                      <span className="text-white text-sm">Android</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={config.target_ios}
                        onChange={(e) => updateConfig({ target_ios: e.target.checked })}
                        className="w-4 h-4 text-cyan-600 bg-slate-700 border-slate-600 rounded focus:ring-cyan-500"
                      />
                      <span className="text-white text-sm">iOS</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={config.target_macos}
                        onChange={(e) => updateConfig({ target_macos: e.target.checked })}
                        className="w-4 h-4 text-cyan-600 bg-slate-700 border-slate-600 rounded focus:ring-cyan-500"
                      />
                      <span className="text-white text-sm">macOS</span>
                    </label>
                  </div>
                </div>

                {/* Excluded Countries */}
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h3 className="text-white font-medium mb-4">Исключенные страны</h3>
                  <p className="text-slate-400 text-sm mb-4">Пользователи из этих стран НЕ увидят рекламу Hidezen</p>
                  <div className="grid grid-cols-3 gap-3">
                    {availableCountries.map(country => (
                      <label key={country.code} className="flex items-center space-x-2 text-sm">
                        <input
                          type="checkbox"
                          checked={config.excluded_countries?.includes(country.code)}
                          onChange={() => toggleCountryExclusion(country.code)}
                          className="w-4 h-4 text-cyan-600 bg-slate-700 border-slate-600 rounded focus:ring-cyan-500"
                        />
                        <span className="text-white">{country.code}</span>
                        <span className="text-slate-400">({country.name})</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Hidezen Configuration */}
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h3 className="text-white font-medium mb-4">Настройки Hidezen VPN</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-slate-300 mb-2">Hidezen URL</label>
                      <input
                        type="url"
                        value={config.hidezen_url}
                        onChange={(e) => updateConfig({ hidezen_url: e.target.value })}
                        className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-300 mb-2">Сообщение пользователям</label>
                      <textarea
                        value={config.hidezen_message}
                        onChange={(e) => updateConfig({ hidezen_message: e.target.value })}
                        className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-400"
                        rows="3"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-300 mb-2">Текст кнопки</label>
                      <input
                        type="text"
                        value={config.hidezen_button_text}
                        onChange={(e) => updateConfig({ hidezen_button_text: e.target.value })}
                        className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-400"
                        placeholder="Получить Hidezen VPN (90 дней бесплатно)"
                      />
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="text-center">
                  <Button
                    onClick={saveConfig}
                    disabled={loading}
                    className="px-8 py-3 text-lg font-bold rounded-xl bg-green-600 hover:bg-green-500 text-white disabled:opacity-50"
                  >
                    💾 {loading ? 'Сохранение...' : 'Сохранить настройки'}
                  </Button>
                  <p className="text-slate-400 text-sm mt-2">
                    Настройки будут применены немедленно
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-slate-400 text-center py-8">
                <p>Загрузка конфигурации...</p>
              </div>
            )}
            </CardContent>
          </Card>
      </div>
    </div>
  );
};

export default AdminPanel;